import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import CountUp from 'react-countup';
import axios from 'axios';
import { AppDeleteModal, AppRemixIcon } from '@/layout/AppComponents';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import AppLink from '../../../../../../routing/helpers/AppLink';
import { axiosPath, path } from '../../../../../../routing/helpers/router';
import colors from '../../../../../../constants/colors';
import Translator from '../../../../../components/helpers/Translator';
import useIsMounted from '../../../../../components/hooks/useIsMounted';

const PlantCardDefault = (props) => {
  const { plant, plantExtended, moduleBridgeHex } = props;
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const auth = useSelector((state) => state.auth);
  const [showDeletePlantModal, setShowDeletePlantModal] = useState(false);
  const autoResetState = useSelector(
    (state) => state.currentPage?.permissions?.gcl?.module?.autoResetState
  );
  const permissions = useSelector((state) => state.currentPage?.permissions);
  const userBridgePermissions = useSelector(
    (state) => state.currentPage?.userBridgePermissions
  );
  const moduleBridgeSettings = useSelector(
    (state) => state.currentPage?.moduleBridgeSettings
  );

  // > LOAD LAYOUT
  const handleAutoResetState = async () => {
    try {
      await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantSelect_resetPlantState"
        ),
        {
          moduleBridgeHex,
          plantHex: plant.hex,
        }
      );
      navigate(
          path({
            name: "user_module_legalCompliance_evaluation",
            params: {
              moduleBridgeHex,
              plantHex: plant.hex,
              page: 1,
              filtersHex: 0,
            },
          })
      );
    } catch (err) {}
  };

  const handleErasePlant = useCallback(async () => {
    await axios
      .post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantSelect_autoClone_delete"
        ),
        {
          plantHex: plant.hex,
        }
      )
      .then(() => {
        if (isMounted.current) {
          location.reload();
        }
      })
      .catch((e) => {});
  }, [plant]);

  return (
    <>
      <Container className="mb-2">
        <div className="pl-name">
          <div className="img-cnt d-flex align-items-center">
            {plantExtended && (
              <img
                className="img-fluid"
                src={
                  plantExtended
                    ? plantExtended.image || auth.user.client.logoUrl
                    : null
                }
                alt={plant.name}
              />
            )}
          </div>
          <div>
            {autoResetState ? (
              <span
                className="wl-title wl-title-md wl-title-color font-weight-bold pr-3 pointer"
                onClick={handleAutoResetState}
              >
                {plant.name}
              </span>
            ) : (
              <AppLink
                path="user_module_legalCompliance_evaluation"
                params={{
                  moduleBridgeHex,
                  plantHex: plant.hex,
                  filtersHex: plant.prefilter,
                }}
                className="wl-title wl-title-md wl-title-color font-weight-bold pr-3"
              >
                {plant.name}
              </AppLink>
            )}
            <br />
            {permissions?.gcl?.module?.plant?.lastVerification && (
              <div className="gcl-plantcard-default-events-block">
                <b>
                  <Translator
                    file="module-legalCompliance"
                    name="latest.verification"
                  />
                  :
                </b>
                <br />{' '}
                {plantExtended ? plantExtended.lastVerification || '---' : '...'}
              </div>
            )}
            {permissions?.gcl?.module?.plant?.reports && (
              <div className="gcl-plantcard-default-events-block">
                <b>
                  <Translator file="module-legalCompliance" name="latest.milestone" />
                  :
                </b>
                <br /> {plantExtended ? plantExtended.lastMilestone || '---' : '...'}
              </div>
            )}
            {permissions?.gcl?.module?.plant?.calendar && (
              <div className="gcl-plantcard-default-events-block">
                <b>
                  <Translator file="module-legalCompliance" name="next.event" />:
                </b>
                <br /> {plantExtended ? plantExtended.nextEvent || '---' : '...'}
              </div>
            )}
            {permissions?.gcl?.module?.plant?.lastChange && (
              <div className="gcl-plantcard-default-events-block">
                <b>
                  <Translator file="module-legalCompliance" name="last.change" />:
                </b>
                <br /> {plantExtended ? plantExtended.lastChange || '---' : '...'}
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center flex-column">
          {permissions?.gcl?.module?.plant?.states && (
            <>
              <div className="progress mt-4">
                {plantExtended?.states &&
                  plantExtended.states.map((s, x) => (
                    <OverlayTrigger
                      key={x}
                      placement="top"
                      overlay={<Tooltip>{`${s.state.name} - ${s.percent}%`}</Tooltip>}
                    >
                      <div
                        key={x}
                        className="progress-bar"
                        style={{
                          width: `${s.percent}%`,
                          background: s.state.color,
                        }}
                        title={`${s.state.name} - ${s.percent}%`}
                      />
                    </OverlayTrigger>
                  ))}
                {plantExtended?.customStates &&
                  plantExtended.customStates.map((s, x) => (
                    <OverlayTrigger
                      key={x}
                      placement="top"
                      overlay={<Tooltip>{`${s.state.name} - ${s.percent}%`}</Tooltip>}
                    >
                      <div
                        key={x}
                        className="progress-bar"
                        style={{
                          width: `${s.percent}%`,
                          background: s.state.color,
                        }}
                        title={`${s.state.name} - ${s.percent}%`}
                      />
                    </OverlayTrigger>
                  ))}
              </div>
              <div
                className="d-flex justify-content-end flex-row-reverse"
                style={{ width: '100%' }}
              >
                {plantExtended?.states &&
                  plantExtended.states.map(
                    (s, i) =>
                      [2, 3].includes(s.state.code) && (
                        <div
                          key={i}
                          style={{
                            display: 'inline-block',
                            width: '120px',
                            color:
                              s.count > 0
                                ? s.state.code === 3
                                  ? colors.danger
                                  : colors.state2
                                : colors.grey400,
                          }}
                          title={
                            s.state.code === 3
                              ? ReactDOMServer.renderToString(
                                  <Translator
                                    file="user"
                                    name="user.obligations.state3"
                                  />,
                                )
                              : ReactDOMServer.renderToString(
                                  <Translator
                                    file="user"
                                    name="user.obligations.state2"
                                  />,
                                )
                          }
                          className="d-flex align-content-center"
                        >
                          <AppRemixIcon
                            icon={s.state.code === 3 ? 'spam' : 'settings-2'}
                            className="mr-1"
                          />
                          <CountUp
                            end={parseInt(s.count || 0)}
                            duration={1}
                            useEasing={false}
                            className="mr-1"
                          />
                          (
                          <CountUp
                            end={parseFloat(s.percent || 0)}
                            duration={1}
                            decimals={2}
                            useEasing={false}
                          />
                          %)
                        </div>
                      ),
                  )}
              </div>
            </>
          )}
        </div>
        <div className="d-flex align-items-center justify-content-end">
          <div className="btn-group float-right">
            {permissions?.gcl?.module?.plant?.calendar?.yearCalendar && (
              <AppLink
                path="user_module_legalCompliance_calendar"
                params={{ moduleBridgeHex, plantHex: plant.hex }}
                className="btn btn-outline-primary"
                title={ReactDOMServer.renderToString(
                  <Translator file="user" name="user.calendar" />,
                )}
              >
                <AppRemixIcon icon="calendar" />
              </AppLink>
            )}
            {permissions?.gcl?.module?.plant?.calendar?.customEmailNotifications &&
              userBridgePermissions?.permissions?.email && (
                <AppLink
                  path="user_module_legalCompliance_plantConfiguration"
                  params={{ moduleBridgeHex, plantHex: plant.hex }}
                  className="btn btn-outline-primary"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="custom.notifications"
                    />,
                  )}
                >
                  <AppRemixIcon icon="mail-settings" />
                </AppLink>
              )}
            {permissions?.gcl?.module?.plant?.stageGates &&
              moduleBridgeSettings?.stageGates?.options?.[plant.hex]
                ?.isProjectClosed && (
                <span title="Closed project" className="btn btn-success">
                  <AppRemixIcon icon="check" />
                </span>
              )}
            {permissions?.gcl?.module?.plant?.userObligationForm &&
              plantExtended?.hasObligationForm && (
                <AppLink
                  path="user_module_legalCompliance_plantObligationForm_manage"
                  params={{
                    clientID: plantExtended.clientID,
                    moduleBridgeHex,
                    plantHex: plant.hex,
                  }}
                  className="btn btn-primary"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligations.form"
                    />,
                  )}
                >
                  <AppRemixIcon icon="file-edit" />
                </AppLink>
              )}
            {plantExtended?.subModuleBridgeComsaReportsHex &&
              (!permissions?.gcl?.module?.plant?.indicators?.visibility ||
                (Array.isArray(
                  permissions?.gcl?.module?.plant?.indicators?.visibility?.users,
                ) &&
                  permissions.gcl.module.plant.indicators.visibility.users.includes(
                    auth.user.id,
                  ))) && (
                <AppLink
                  path="user_module_comsaReports_home"
                  params={{
                    moduleBridgeHex,
                    subModuleBridgeHex: plantExtended.subModuleBridgeComsaReportsHex,
                    plantHex: plant.hex,
                  }}
                  className="btn btn-outline-primary"
                  title={ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  )}
                >
                  <AppRemixIcon icon="file-text" />
                </AppLink>
              )}
            {plantExtended?.subModuleBridgeComsaEnvironmentalHex && (
              <AppLink
                path="user_module_comsaEnvironmental_home"
                params={{
                  moduleBridgeHex,
                  subModuleBridgeHex:
                    plantExtended.subModuleBridgeComsaEnvironmentalHex,
                  plantHex: plant.hex,
                }}
                className="btn btn-outline-primary"
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspects"
                  />,
                )}
              >
                <AppRemixIcon icon="leaf" />
              </AppLink>
            )}
            {plantExtended?.subModuleBridgeQseReportsHex && (
              <AppLink
                path="user_module_qseReports_home"
                params={{
                  moduleBridgeHex,
                  subModuleBridgeHex: plantExtended.subModuleBridgeQseReportsHex,
                  plantHex: plant.hex,
                }}
                className="btn btn-outline-primary"
                title="QHSE monthly report"
              >
                <AppRemixIcon icon="file-text" />
              </AppLink>
            )}
            {plantExtended?.isAutoClone && plantExtended?.isAllowDeletingCloned && (
              <>
                <AppDeleteModal
                  show={showDeletePlantModal}
                  title="Desactivar instalación"
                  size="lg"
                  onClose={() => setShowDeletePlantModal(false)}
                  onConfirm={() => handleErasePlant()}
                  sensitive
                  sensitiveWord="desactivar"
                />
                <span
                  className="btn btn-danger"
                  title="Desactivar instalación"
                  onClick={() => setShowDeletePlantModal(true)}
                >
                  <AppRemixIcon icon="delete-bin" />
                </span>
              </>
            )}
          </div>
        </div>
      </Container>
      <hr />
    </>
  );
};

export default PlantCardDefault;

const Container = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, 1fr) 330px 240px;

  .pl-name{
  display: grid;
    grid-template-columns: 86px 1fr;
    .img-cnt{
      width: 80px;
      height: 80px;
      background: ${colors.grey300};
      img{
        border-radius: 4px;
        margin-right: 6px;
      }
    }
  }

  .gcl-plantcard-default-events-block {
    display: inline-block;
    padding-top: 10px;
    padding-right: 20px;
    //width: 45%;
    font-size: 11px;
  }
  .progress-container{
    display: flex;
    align-items: center;
    height: 90px;
  }
  .progress{
    border-radius: 3px;
    height: 16px;
    width: 100%;
  }
}

`;
